import { Modal } from "@shopify/app-bridge-react";
import {
  Card,
  ChoiceList,
  EmptyState,
  Filters,
  IndexTable,
  Pagination,
  useIndexResourceState,
  TextField,
  Link,
  Modal as PolarisModal,
  Text,
} from "@shopify/polaris";
import { useQuery } from "@tanstack/react-query";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppContext } from "../../../components/global/appContext";
import { useCreateBulkCSVExport } from "../../../hooks/queries/locations/bulkCSVExport";
import {
  useCreateBulkUpdate,
  useDestroyBulkUpdate,
} from "../../../hooks/queries/locations/bulkUpdate";
import useDebounce from "../../../hooks/useDebounce";
import lmsAPI from "../../../services/lmsAPI";
import PlaceTableSkeleton from "./placeTableSkeleton";
import PriorityField from "./PriorityField";

export default function PlaceTable(props) {
  const { t } = useTranslation();
  
  const SORT_OPTIONS = [
    { label: t("location.table.sortOptions.recentlyAdded"), value: "created_at" },
    { label: t("location.table.sortOptions.recentlyModified"), value: "updated_at" },
  ];
  const appContext = useContext(AppContext);
  const [deleteLocationsModalOpen, setDeleteLocationsModalOpen] =
    useState(false);

  const [prioritiseLocationsModalOpen, setPrioritiseLocationsModalOpen] =
    useState(false);
  const [prioritiseLocationsPriority, setPrioritiseLocationsPriority] =
    useState(1);

  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    //sort_value: SORT_OPTIONS[0].value,
  });

  let queryParamsWithSearch = useMemo(() => {
    return { ...queryParams, search: debouncedSearchValue };
  }, [queryParams, debouncedSearchValue]);

  async function fetchLocations(queryParams) {
    const res = await lmsAPI.get(props.url, { params: queryParams });
    return res.data;
  }

  const { data, isError, isLoading } = useQuery(
    [props.resourceName.plural, queryParamsWithSearch],
    () => fetchLocations(queryParamsWithSearch),
    { keepPreviousData: true }
  );

  const {
    selectedResources,
    allResourcesSelected,
    handleSelectionChange,
    clearSelection,
  } = useIndexResourceState(data?.data || []);

  // Actions
  const createBulkUpdateMutation = useCreateBulkUpdate({
    onSuccess: () => {
      appContext.setMessage(t("location.table.messages.updatedSuccessfully"));
      clearSelection();
    },
    onError: (error) => {
      appContext.setMessage(error.response.data.error, true);
    },
  });

  const bulkDeleteMutation = useDestroyBulkUpdate({
    onSuccess: () => {
      appContext.setMessage(t("location.table.messages.deletedSuccessfully"));
      clearSelection();
    },
    onError: (error) => {
      appContext.setMessage(error.response.data.error, true);
    },
  });

  const exportLocationsMutation = useCreateBulkCSVExport({
    onSuccess: (data) => {
      // Download the file
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "locations.csv");
      document.body.appendChild(link);
      link.click();
      appContext.setMessage(t("location.table.messages.exportedSuccessfully"));
      clearSelection();
    },
    onError: (error) => {
      appContext.setMessage(error.response.data.error, true);
    },
  });

  const publishLocations = (selectedResourcesParam) =>
    createBulkUpdateMutation.mutate({
      locationIDs: selectedResourcesParam,
      updateParams: { status: "published" },
    });

  const unpublishLocations = (selectedResourcesParam) =>
    createBulkUpdateMutation.mutate({
      locationIDs: selectedResourcesParam,
      updateParams: { status: "draft" },
    });

  const prioritiseLocations = (selectedResourcesParam, priority) =>
    createBulkUpdateMutation.mutate(
      {
        locationIDs: selectedResourcesParam,
        updateParams: { priority: priority },
      },
      {
        onSuccess: () => setPrioritiseLocationsModalOpen(false),
      }
    );

  const deleteLocations = (selectedResourcesParam) =>
    bulkDeleteMutation.mutate(
      { locationIDs: selectedResourcesParam },
      {
        onSuccess: () => setDeleteLocationsModalOpen(false),
      }
    );

  const exportLocations = (selectedResourcesParam) =>
    exportLocationsMutation.mutate(selectedResourcesParam);

  let bulkActionProps = [];
  let filters = [];

  const selectedResourcesParam = useMemo(
    () => (allResourcesSelected ? "All" : selectedResources),
    [selectedResources, allResourcesSelected]
  );

  if (props.isLocationList && data) {
    bulkActionProps = {
      bulkActions: [
        {
          content: t("location.table.bulkActions.setPublished"),
          onAction: () => publishLocations(selectedResourcesParam),
          loading: createBulkUpdateMutation.isLoading,
        },
        {
          content: t("location.table.bulkActions.setUnpublished"),
          onAction: () => unpublishLocations(selectedResourcesParam),
          loading: createBulkUpdateMutation.isLoading,
        },
        {
          content: t("location.table.bulkActions.setPriority"),
          onAction: () => setPrioritiseLocationsModalOpen(true),
          loading: createBulkUpdateMutation.isLoading,
        },
        {
          content: t("location.table.bulkActions.deleteLocations"),
          destructive: true,
          onAction: () => setDeleteLocationsModalOpen(true),
          loading: bulkDeleteMutation.isLoading,
        },
      ],
      promotedBulkActions: [
        {
          content: t("location.table.bulkActions.exportCSV"),
          onAction: () => exportLocations(selectedResourcesParam),
          loading: exportLocationsMutation.isLoading,
        },
      ],
    };

    filters = [
      {
        key: "status",
        label: t("location.table.filters.status"),
        filter: (
          <ChoiceList
            title={t("location.table.filters.locationStatus")}
            titleHidden
            choices={[
              { label: t("location.index.draft"), value: "draft" },
              { label: t("location.index.published"), value: "published" },
            ]}
            selected={[queryParams.status]}
            onChange={(value) => updateQueryParam("status", value[0])}
          />
        ),
        shortcut: true,
      },
      {
        key: "taggedWith",
        label: t("location.table.filters.taggedWith"),
        filter: data.all_tags.length ? (
          <ChoiceList
            title={t("location.table.filters.taggedWith")}
            titleHidden
            allowMultiple
            choices={data.all_tags}
            selected={queryParams.tagged_with || []}
            onChange={(value) => updateQueryParam("tagged_with", value)}
          />
        ) : (
          <Text  variant="bodyMd" as="span" color="subdued">{t("location.table.filters.noTagsToShow")}</Text>
        ),
        hideClearButton: true,
        shortcut: true,
      },
    ];
  }

  const deleteLocationsModalMarkup = (
    <Modal
      open={deleteLocationsModalOpen}
      onClose={() => setDeleteLocationsModalOpen(false)}
      title={t("location.table.modals.deleteTitle")}
      message={allResourcesSelected 
        ? t("location.table.modals.deleteMessageAll")
        : t("location.table.modals.deleteMessage", { count: "these locations" })
      }
      primaryAction={{
        content: t("location.table.modals.delete"),
        destructive: true,
        onAction: () => {
          deleteLocations(selectedResourcesParam);
        },
        loading: bulkDeleteMutation.isLoading,
      }}
      secondaryActions={[
        {
          content: t("location.table.modals.cancel"),
          onAction: () => setDeleteLocationsModalOpen(false),
        },
      ]}
    />
  );

  const prioritiseLocationsModalMarkup = (
    <PolarisModal
      open={prioritiseLocationsModalOpen}
      // open={true}
      onClose={() => setPrioritiseLocationsModalOpen(false)}
      title={allResourcesSelected 
        ? t("location.table.modals.prioritiseTitleAll")
        : t("location.table.modals.prioritiseTitle", { count: selectedResources.length })
      }
      primaryAction={{
        content: t("location.table.bulkActions.setPriority"),
        onAction: () =>
          prioritiseLocations(
            selectedResourcesParam,
            prioritiseLocationsPriority
          ),
        loading: createBulkUpdateMutation.isLoading,
      }}
      secondaryActions={[
        {
          content: t("location.table.modals.cancel"),
          onAction: () => setPrioritiseLocationsModalOpen(false),
        },
      ]}
    >
      <PolarisModal.Section>
        <PriorityField
          onChange={setPrioritiseLocationsPriority}
          value={prioritiseLocationsPriority}
        />
      </PolarisModal.Section>
    </PolarisModal>
  );

  // Displaying filters
  const updateQueryParam = (paramKey, value) => {
    setQueryParams((oldParams) => {
      return { ...oldParams, [paramKey]: value };
    });
  };

  const capitalize = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const tagIdToTitle = (id) => {
    const tag = data.all_tags.find((tag) => tag.value == id);
    if (tag) return tag.label;
  };

  const removeTag = (id) => {
    let newTags = [...queryParams.tagged_with].filter((tagId) => tagId !== id);
    updateQueryParam("tagged_with", newTags);
  };

  let appliedFilters = useMemo(() => {
    let filters = [];
    if (queryParams.status) {
      filters.push({
        key: "status",
        label: t("location.table.appliedFilters.statusIs", { status: capitalize(queryParams.status) }),
        onRemove: () => updateQueryParam("status", undefined),
      });
    }

    if (queryParams.tagged_with) {
      queryParams.tagged_with.forEach((tagId) => {
        filters.push({
          key: `taggedWith${tagId}`,
          label: t("location.table.appliedFilters.taggedWith", { tag: tagIdToTitle(tagId) }),
          onRemove: () => removeTag(tagId),
        });
      });
    }

    if (debouncedSearchValue) {
      filters.push({
        key: "search",
        label: t("location.table.appliedFilters.nameIncludes", { search: debouncedSearchValue }),
        onRemove: () => setSearchValue(""),
      });
    }

    return filters;
  }, [queryParams.status, queryParams.tagged_with, debouncedSearchValue]);

  const filterControl = (
    <Filters
      filters={filters}
      appliedFilters={appliedFilters}
      queryValue={searchValue}
      queryPlaceholder={t("location.table.filters.searchPlaceholder")}
      onQueryChange={setSearchValue}
      onQueryClear={() => setSearchValue("")}
    />
  );

  // Set page to 1 if there are any filters applied
  useEffect(() => {
    if (appliedFilters.length > 0 && queryParams.page !== 1)
      updateQueryParam("page", 1);
  }, [appliedFilters]);

  if (isError) {
    return (
      <Card.Section>
        <p>{t("location.table.messages.error")}</p>
      </Card.Section>
    );
  }

  if (!data) return <PlaceTableSkeleton headings={props.headings} />;

  const showPagination =
    (data.pagination.prev_url || data.pagination.next_url) &&
    !debouncedSearchValue;

  // Empty state
  const emptyStateMarkup = appliedFilters.length ? (
    <EmptyState
      heading={t("location.table.emptyState.noResultsHeading", { resourcePlural: props.resourceName.plural })}
      image="https://cdn.shopify.com/s/files/1/2376/3301/products/emptystate-files.png"
    >
      <p>{t("location.table.emptyState.noResultsText")}</p>
    </EmptyState>
  ) : (
    <EmptyState
      heading={t("location.table.emptyState.noDataHeading", { resourcePlural: props.resourceName.plural })}
      action={{ content: t("location.table.emptyState.addLocation"), url: "/places/locations/new" }}
      secondaryAction={{
        content: t("location.table.emptyState.importFromCSV"),
        url: "/places/upload/new",
      }}
      image="https://cdn.shopify.com/s/files/1/2376/3301/products/emptystate-files.png"
    ></EmptyState>
  );

  // Displaying table
  const placeMarkup = data.data.map((place) =>
    props.renderLocation(
      place,
      allResourcesSelected ? true : selectedResources.includes(place.id)
    )
  );

  return (
    <div className="place-table-wrapper">
      <Card>
        <Card.Section>{filterControl}</Card.Section>

        <Card.Section flush>
          {/* <Scrollable shadow focusable> */}
          <IndexTable
            loading={isLoading}
            {...bulkActionProps}
            resourceName={props.resourceName}
            selectable={props.isLocationList}
            selectedItemsCount={
              allResourcesSelected ? "All" : selectedResources.length
            }
            onSelectionChange={handleSelectionChange}
            emptyState={emptyStateMarkup}
            headings={props.headings}
            hasMoreItems={data.pagination.pages > 1}
            itemCount={data.pagination.count}
            isFiltered={appliedFilters.length}
            sortOptions={SORT_OPTIONS}
            // sortValue={queryParams.sort_value}
            // onSortChange={(value) => updateQueryParam("sort_value", value)}
          >
            {placeMarkup}
          </IndexTable>
        </Card.Section>
        {showPagination && (
          <Card.Section key="aaa">
            <div className="pagination-wrapper">
              <Pagination
                hasPrevious={data.pagination.prev_url}
                hasNext={data.pagination.next_url}
                label={
                  <Text  variant="bodyMd" as="span" color="subdued">
                    {t("location.table.pagination.label", { page: data.pagination.page, pages: data.pagination.pages })}
                  </Text>
                }
                onPrevious={() =>
                  updateQueryParam("page", data.pagination.prev)
                }
                onNext={() => updateQueryParam("page", data.pagination.next)}
              />
            </div>
          </Card.Section>
        )}
      </Card>
      {deleteLocationsModalMarkup}
      {prioritiseLocationsModalMarkup}
    </div>
  );
}

// A good description of how i should refactor this below
